/**
 * Images editor — the "Images" module that owns Shopify's Image Src /
 * Image Position / Image Alt Text columns.
 *
 * Shopify's product CSV takes images one per row: the first image goes on the
 * product row, every further image on its own row carrying only the Handle,
 * Image Src, Image Position and Image Alt Text. Supplier sheets usually have
 * Image 1 … Image N columns instead, so here the user lists those columns in
 * order (order = position), optionally rewrites the link around a column
 * (CDN prefix + file name), and sets alt text per image or for all of them.
 * The engine (`collectImageEntries` + the Shopify fanout) does the rest.
 */

import { useMemo, useRef, type ReactNode } from "react";
import {
  collectImageEntries,
  detectImageSlots,
  fieldValue,
  imageSlotsOf,
  slotColumn,
  slotSourceColumns,
} from "@/lib/mapper/images";
import type {
  ImageConfig,
  ImageSlot,
  ShopifyRow,
  SourceRow,
  TemplateValue,
} from "@/lib/mapper/types";
import {
  FieldInput,
  type FieldInputHandle,
} from "@/modules/_shared/FieldInput.client";

type Props = {
  images: ImageConfig | undefined;
  onChange: (next: ImageConfig) => void;
  sourceHeaders: string[];
  outputColumns: string[];
  sampleRow: SourceRow | undefined;
  /** Resolved product row for the sample (Title etc.) — powers the alt preview. */
  sampleOutput?: ShopifyRow;
  registerFocusedAutocomplete: (handle: FieldInputHandle | null) => void;
};

const LABEL =
  "text-[10.5px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400";

/** Empty template → undefined, so "no alt" stays absent in the saved template. */
function orUndefined(v: TemplateValue): TemplateValue | undefined {
  return v.some((seg) => seg.t === "field" || seg.v.trim().length > 0) ? v : undefined;
}

export function ImagesEditor({
  images,
  onChange,
  sourceHeaders,
  outputColumns,
  sampleRow,
  sampleOutput,
  registerFocusedAutocomplete,
}: Props) {
  const slots = useMemo(() => imageSlotsOf(images), [images]);
  const transform = images?.transform ?? "dropbox-direct";
  const fieldProps = { sourceHeaders, outputColumns, sampleRow, registerFocusedAutocomplete };

  function commit(next: Partial<ImageConfig> & { slots?: ImageSlot[] }) {
    const nextSlots = next.slots ?? slots;
    onChange({
      ...(images ?? { sourceColumns: [], transform }),
      ...next,
      slots: nextSlots,
      // Kept in sync so older readers still see the plain image columns.
      sourceColumns: slotSourceColumns(nextSlots),
    });
  }

  const setSlot = (i: number, patch: Partial<ImageSlot>) =>
    commit({ slots: slots.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const removeSlot = (i: number) => commit({ slots: slots.filter((_, j) => j !== i) });
  const moveSlot = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= slots.length) return;
    const next = [...slots];
    [next[i], next[j]] = [next[j], next[i]];
    commit({ slots: next });
  };
  const addColumn = (column: string) => {
    if (!column) return;
    commit({ slots: [...slots, { src: fieldValue(column) }] });
  };

  const usedColumns = new Set(slots.map(slotColumn).filter(Boolean) as string[]);
  const detected = detectImageSlots(sourceHeaders).filter((s) => {
    const col = slotColumn(s);
    return col !== undefined && !usedColumns.has(col);
  });

  const preview = useMemo(() => {
    if (!sampleRow || !images) return [];
    const fallback = sampleOutput?.["Title"] ?? "";
    return collectImageEntries(sampleRow, { ...images, slots }, { output: sampleOutput }).map(
      (e) => ({ ...e, alt: e.alt || fallback }),
    );
  }, [sampleRow, sampleOutput, images, slots]);

  return (
    <div className="space-y-4">
      <p className="text-[11.5px] leading-snug text-slate-600 dark:text-slate-400">
        Shopify takes one image per row: the first image sits on the product row
        and each extra image gets its own row with the same Handle. List the
        columns that hold your image links <strong>in the order you want</strong>;
        that order becomes the Image Position.
      </p>

      {/* Image list */}
      <div>
        <div className="flex items-center justify-between">
          <span className={LABEL}>Images, in position order</span>
          <span className="text-[10.5px] text-slate-400 dark:text-slate-500">
            {slots.length} image source{slots.length === 1 ? "" : "s"}
          </span>
        </div>

        {slots.length === 0 && (
          <p className="mt-2 rounded border border-dashed border-slate-300 dark:border-border px-3 py-3 text-center text-[11.5px] text-slate-500 dark:text-slate-400">
            No image columns yet. Add one below.
          </p>
        )}

        <ol className="mt-2 space-y-2">
          {slots.map((slot, i) => (
            <li
              key={i}
              className="rounded-md border border-slate-200 dark:border-border bg-white dark:bg-surface p-2"
            >
              <div className="flex items-center gap-2">
                <span
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-teal-50 font-mono text-[11px] font-semibold text-teal-800 ring-1 ring-teal-200 dark:bg-teal-950/40 dark:text-teal-300 dark:ring-teal-800"
                  title={`Image Position ${i + 1}`}
                >
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <TemplateField
                    {...fieldProps}
                    value={slot.src}
                    onChange={(v) => setSlot(i, { src: v })}
                    placeholder="/Image 1  or  https://cdn.example.com//File name"
                  />
                </div>
                <div className="flex shrink-0 items-center">
                  <IconButton label="Move up" disabled={i === 0} onClick={() => moveSlot(i, -1)}>
                    ↑
                  </IconButton>
                  <IconButton
                    label="Move down"
                    disabled={i === slots.length - 1}
                    onClick={() => moveSlot(i, 1)}
                  >
                    ↓
                  </IconButton>
                  <IconButton label="Remove image" danger onClick={() => removeSlot(i)}>
                    ✕
                  </IconButton>
                </div>
              </div>
              <div className="mt-1.5 flex items-center gap-2 pl-8">
                <span className="w-7 shrink-0 text-[10.5px] font-medium text-slate-500 dark:text-slate-400">
                  Alt
                </span>
                <div className="min-w-0 flex-1">
                  <TemplateField
                    {...fieldProps}
                    value={slot.alt ?? ""}
                    onChange={(v) => setSlot(i, { alt: orUndefined(v) })}
                    placeholder="Same as all images (below)"
                  />
                </div>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select
            value=""
            onChange={(e) => addColumn(e.target.value)}
            aria-label="Add image column"
            className="rounded border border-teal-300 dark:border-teal-800 bg-white dark:bg-surface px-2 py-1.5 text-[12px] font-medium text-teal-800 dark:text-teal-300 focus:outline-none focus:ring-1 focus:ring-teal-300"
          >
            <option value="">+ Add image column…</option>
            {sourceHeaders.map((h) => (
              <option key={h} value={h}>
                {usedColumns.has(h) ? `${h} (already added)` : h}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => commit({ slots: [...slots, { src: "" }] })}
            className="rounded border border-dashed border-slate-300 dark:border-border px-2 py-1.5 text-[11.5px] text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-surface-2"
            title="An image link built from text + columns, e.g. a CDN address + a file-name column"
          >
            + Custom link
          </button>
          {detected.length > 0 && (
            <button
              type="button"
              onClick={() => commit({ slots: [...slots, ...detected] })}
              className="rounded border border-slate-300 dark:border-border px-2 py-1.5 text-[11.5px] text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-surface-2"
              title={detected.map((s) => slotColumn(s)).join(", ")}
            >
              Add {detected.length} detected column{detected.length === 1 ? "" : "s"}
            </button>
          )}
        </div>
        <p className="mt-1.5 text-[10.5px] leading-snug text-slate-400 dark:text-slate-500">
          Type / in a box to insert a column. A cell with several links (one per
          line, or separated by commas) becomes several images.
        </p>
      </div>

      {/* Default alt */}
      <label className="block">
        <span className={LABEL}>Alt text for all images</span>
        <div className="mt-1">
          <TemplateField
            {...fieldProps}
            value={images?.alt ?? ""}
            onChange={(v) => commit({ alt: orUndefined(v) })}
            placeholder="Blank = the product Title"
          />
        </div>
        <span className="mt-0.5 block text-[10.5px] text-slate-500 dark:text-slate-400">
          Used when an image has no alt of its own. Pick a column (type /) to
          replace the alt text from your sheet. Keep it under 125 characters.
        </span>
      </label>

      <label className="flex items-center gap-2 text-[11.5px] text-slate-700 dark:text-slate-300">
        <input
          type="checkbox"
          checked={transform === "dropbox-direct"}
          onChange={(e) => commit({ transform: e.target.checked ? "dropbox-direct" : "none" })}
          className="accent-teal-600"
        />
        Turn Dropbox share links into direct image links
      </label>

      {/* Preview */}
      {sampleRow && (
        <div>
          <span className={LABEL}>Row 1 becomes</span>
          {preview.length === 0 ? (
            <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
              No image links in row 1.
            </p>
          ) : (
            <table className="mt-1 w-full table-fixed border-collapse text-[11px]">
              <thead>
                <tr className="text-left text-slate-500 dark:text-slate-400">
                  <th className="w-10 py-1 font-medium">Pos.</th>
                  <th className="py-1 font-medium">Image Src</th>
                  <th className="w-[35%] py-1 font-medium">Alt text</th>
                </tr>
              </thead>
              <tbody>
                {preview.slice(0, 10).map((e, i) => (
                  <tr key={i} className="border-t border-slate-100 dark:border-border">
                    <td className="py-1 font-mono text-slate-600 dark:text-slate-300">{i + 1}</td>
                    <td className="truncate py-1 pr-2 font-mono text-slate-700 dark:text-slate-300" title={e.src}>
                      {e.src}
                    </td>
                    <td className="truncate py-1 text-slate-700 dark:text-slate-300" title={e.alt}>
                      {e.alt || <span className="text-slate-400">(blank)</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {preview.length > 10 && (
            <p className="mt-1 text-[10.5px] text-slate-400">+{preview.length - 10} more</p>
          )}
        </div>
      )}
    </div>
  );
}

/** One-line template input that registers itself for "/" column insertion while focused. */
function TemplateField({
  value,
  onChange,
  placeholder,
  sourceHeaders,
  outputColumns,
  sampleRow,
  registerFocusedAutocomplete,
}: {
  value: TemplateValue | string;
  onChange: (next: TemplateValue) => void;
  placeholder: string;
  sourceHeaders: string[];
  outputColumns: string[];
  sampleRow: SourceRow | undefined;
  registerFocusedAutocomplete: (handle: FieldInputHandle | null) => void;
}) {
  const ref = useRef<FieldInputHandle | null>(null);
  return (
    <FieldInput
      ref={ref}
      value={value}
      onChange={onChange}
      sourceHeaders={sourceHeaders}
      outputColumns={outputColumns}
      sampleRow={sampleRow}
      placeholder={placeholder}
      onFocus={() => registerFocusedAutocomplete(ref.current)}
      onBlur={() => registerFocusedAutocomplete(null)}
      hideHelp
    />
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  danger,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={[
        "rounded px-1.5 py-1 text-[12px] leading-none text-slate-400 transition disabled:opacity-30",
        danger
          ? "hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40"
          : "hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-surface-2 dark:hover:text-slate-100",
      ].join(" ")}
    >
      {children}
    </button>
  );
}
